import { z } from "zod";
import { zId, zInstanteUtc, zTipoItem } from "./dominio.js";
import { blocoDoFormato } from "./pedido-de-itens.js";
import { zMarcado } from "./estudo.js";
import { agruparPorMateriaEAssunto } from "./materia.js";
/**
 * Diagnóstico de erros — RN-46.
 *
 * **Não é a fila de erradas.** A fila é a lista do que praticar em seguida,
 * ordenada pelo score da RN-45; isto é o retrato de *onde* se erra, agrupado por
 * matéria e assunto, com a alternativa que foi marcada em cada erro. Juntar as
 * duas faria a tela de praticar carregar um relatório, e o relatório perder a
 * pergunta que ele responde.
 *
 * **A alternativa marcada é o dado que faltava.** Ela já existia em
 * `resposta.marcado` desde a F1 e nunca tinha sido mostrada: saber que se errou
 * a questão 12 não diz nada, e saber que se marcou "C" quando a certa era "A"
 * diz qual confusão está acontecendo.
 *
 * **A saída fecha o ciclo.** O Markdown exportado é um pedido pronto para um
 * modelo de linguagem: enunciado, o que foi marcado, o gabarito, a explicação e
 * quantas vezes se errou, com a instrução de devolver JSON no mesmo schema que a
 * importação aceita. Errar → gerar itens novos do ponto fraco → importar.
 */
/**
 * A janela de tempo do diagnóstico.
 *
 * `tudo` e três janelas em dias. Ela é **do servidor**, e não um filtro da tela,
 * porque o que muda com ela não é só a lista: os quatro indicadores, o ranking
 * de assuntos e a taxa por matéria são todos contados dentro da janela. Filtrar
 * a lista no navegador e deixar os números do acervo inteiro seria a tela
 * dizendo duas coisas ao mesmo tempo.
 */
export const PERIODOS_DO_DIAGNOSTICO = ["tudo", "7", "30", "90"];
export const zPeriodoDoDiagnostico = z.enum(PERIODOS_DO_DIAGNOSTICO);
export const zPeriodoParam = z.object({ periodo: zPeriodoDoDiagnostico });
/** O rótulo de cada janela, para a tela não reescrever a tabela. */
export const NOME_DO_PERIODO = {
    tudo: "Tudo",
    "7": "7 dias",
    "30": "30 dias",
    "90": "90 dias",
};
/**
 * O que a tela mostra da lista. É filtro **de exibição**, e por isso fica no
 * navegador: ele não muda nenhum número, só esconde linhas.
 */
export const EXIBICOES_DO_DIAGNOSTICO = ["todas", "pendentes", "recuperadas"];
export const zExibicaoDoDiagnostico = z.enum(EXIBICOES_DO_DIAGNOSTICO);
export const NOME_DA_EXIBICAO = {
    todas: "Todas que errei",
    pendentes: "Só pendentes",
    recuperadas: "Só recuperadas",
};
export const zErroDiagnosticado = z.object({
    itemId: zId,
    tipo: zTipoItem,
    enunciado: z.string(),
    /** Caminho completo. A tela agrupa por matéria e assunto a partir dele. */
    categoria: z.array(z.string()),
    dificuldade: z.string().nullable(),
    explicacao: z.string().nullable(),
    /**
     * As alternativas do item, quando houver. Sem elas, "marcou C" não diz o que
     * é C — e o pedido de itens novos sairia sem o texto da confusão.
     */
    alternativas: z.record(z.string(), z.string()).nullable(),
    /** O gabarito, já revelado: esta tela só existe para item já respondido. */
    gabarito: z.unknown(),
    /** O que foi marcado nas tentativas erradas, da mais recente para a mais antiga. */
    marcacoesErradas: z.array(z.object({ marcado: zMarcado, quando: zInstanteUtc })),
    erros: z.number().int().nonnegative(),
    tentativas: z.number().int().nonnegative(),
    ultimoErroEm: zInstanteUtc,
    /**
     * A última resposta foi **acerto**.
     *
     * É o que separa pendente de recuperada, e é a razão de esta tela ter deixado
     * de ler a fila de erradas: a fila só guarda o que está pendente, então o que
     * foi recuperado sumia do diagnóstico no instante em que deixou de ser
     * problema — justo o item que prova que o estudo funcionou.
     */
    recuperado: z.boolean(),
});
export const zTaxaPorCategoria = z.object({
    caminho: z.array(z.string()),
    certas: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
});
/** Um assunto no ranking "onde mais erro". */
export const zAssuntoComErro = z.object({
    /** Caminho completo; a tela imprime "Matéria › Assunto". */
    caminho: z.array(z.string()),
    /** Questões distintas erradas ao menos uma vez na janela. */
    questoes: z.number().int().nonnegative(),
    /** Respostas erradas na janela — uma questão errada três vezes conta três. */
    erros: z.number().int().nonnegative(),
    /** Respostas ao todo na janela, para a taxa de erro do desempate. */
    respostas: z.number().int().nonnegative(),
});
/** Uma matéria no acordeão. */
export const zMateriaComErro = z.object({
    nome: z.string().nullable(),
    questoes: z.number().int().nonnegative(),
    erros: z.number().int().nonnegative(),
    certas: z.number().int().nonnegative(),
    respostas: z.number().int().nonnegative(),
});
/** Os quatro indicadores da faixa. */
export const zResumoDoDiagnostico = z.object({
    /** Questões distintas erradas ao menos uma vez na janela. */
    erradas: z.number().int().nonnegative(),
    /** Dessas, as que a última resposta ainda foi erro. */
    pendentes: z.number().int().nonnegative(),
    /** Dessas, as que a última resposta já foi acerto. */
    recuperadas: z.number().int().nonnegative(),
    /** Matérias distintas com ao menos uma questão errada. */
    materiasAfetadas: z.number().int().nonnegative(),
});
export const zDiagnosticoDeErros = z.object({
    erros: z.array(zErroDiagnosticado),
    /** As taxas por categoria das estatísticas, para o erro ter contexto. */
    taxas: z.array(zTaxaPorCategoria),
    resumo: zResumoDoDiagnostico,
    /** Top 10 assuntos, por erros e depois por taxa de erro. */
    ranking: z.array(zAssuntoComErro),
    materias: z.array(zMateriaComErro),
    periodo: zPeriodoDoDiagnostico,
    trilha: z.string(),
});
/* ------------------------------------------------------ o texto do marcado */
/**
 * O que foi marcado, em português.
 *
 * O cartão não entra: nele não há certo objetivo, só autoavaliação, e "marcou 2"
 * não é uma confusão a diagnosticar — é a pessoa dizendo que custou lembrar.
 */
export function textoDoMarcado(m, alternativas) {
    switch (m.tipo) {
        case "multipla":
            return alternativas?.[m.alternativa]
                ? `${m.alternativa} — ${alternativas[m.alternativa]}`
                : m.alternativa;
        case "certo_errado":
            return m.valor ? "Certo" : "Errado";
        case "cloze":
            return Object.entries(m.lacunas)
                .map(([chave, valor]) => `${chave}: ${valor || "(em branco)"}`)
                .join(" · ");
        case "cartao":
            return `autoavaliação ${m.autoavaliacao}`;
    }
}
/**
 * O gabarito, em português, no mesmo formato do marcado.
 *
 * **O cloze não guarda resposta em `gabarito`** — ela mora em
 * `conteudo.lacunas[].resposta`, e é de lá que o feedback da execução a tira,
 * por `gabaritoPublico`. O diagnóstico procurava em `gabarito.resposta`, que
 * nunca existiu, e imprimia "(sem gabarito)" em todo item de lacuna. Aqui a
 * forma aceita é a mesma que a execução já entrega: `{ lacunas: [{id, resposta}] }`.
 *
 * `null` quer dizer **não se aplica**, e é diferente de não saber: o cartão não
 * tem certo objetivo, quem julgou foi o usuário. Quem chama esconde a linha em
 * vez de mostrar rótulo vazio.
 */
export function textoDoGabarito(tipo, gabarito, alternativas) {
    const g = (gabarito ?? {});
    switch (tipo) {
        case "multipla": {
            const correta = typeof g.correta === "string" ? g.correta : null;
            if (!correta)
                return "(sem gabarito)";
            return alternativas?.[correta] ? `${correta} — ${alternativas[correta]}` : correta;
        }
        case "certo_errado":
            return g.correta === true ? "Certo" : g.correta === false ? "Errado" : "(sem gabarito)";
        case "cloze": {
            const lacunas = g.lacunas;
            if (!Array.isArray(lacunas) || lacunas.length === 0)
                return "(sem gabarito)";
            return lacunas.map((l) => `${l.id}: ${l.resposta}`).join(" · ");
        }
        default:
            return null;
    }
}
/* ---------------------------------------------------------------- Markdown */
/**
 * O pedido pronto, em Markdown (RN-46).
 *
 * Ele é escrito para ser **colado inteiro** num modelo de linguagem, e por isso
 * carrega três coisas que um relatório comum não carregaria: a instrução do que
 * fazer, o formato exato da resposta, e as categorias com o caminho completo —
 * sem elas o JSON voltaria com categoria inventada e a importação criaria uma
 * árvore paralela.
 *
 * O que ele **não** carrega é o id do item. O arquivo que volta é um lote novo,
 * e id de item existente ali dentro só serviria para colidir na importação.
 */
export function montarPedidoDeItens(d) {
    const partes = [];
    partes.push("# Itens novos para os meus pontos fracos");
    partes.push("");
    partes.push(`Abaixo estão as questões que eu errei na trilha **${d.trilha}**, agrupadas por matéria e ` +
        "assunto, com o que eu marquei e qual era a resposta certa.");
    partes.push("");
    partes.push("Gere **questões novas** sobre exatamente as confusões que aparecem aqui — não repita as " +
        "questões abaixo. Devolva **apenas um bloco de JSON**, no formato de importação do Grifo, " +
        "usando os caminhos de categoria exatamente como estão escritos aqui.");
    partes.push("");
    if (d.erros.length === 0) {
        partes.push("_Nenhum erro pendente. Não há o que gerar._");
        partes.push("");
        return partes.join("\n");
    }
    for (const materia of agruparPorMateriaEAssunto(d.erros, (e) => e.categoria)) {
        partes.push(`## ${materia.materia ?? "Sem matéria"}`);
        partes.push("");
        for (const assunto of materia.itens) {
            partes.push(`### ${assunto.materia ?? "(direto na matéria)"}`);
            partes.push("");
            const taxa = d.taxas.find((t) => t.caminho.join(" › ") === assunto.itens[0]?.categoria.join(" › "));
            if (taxa && taxa.total > 0) {
                partes.push(`Acerto neste assunto: **${taxa.certas} de ${taxa.total}**.`);
                partes.push("");
            }
            for (const e of assunto.itens) {
                partes.push(`- **Enunciado:** ${umaLinha(e.enunciado)}`);
                const marcada = e.marcacoesErradas[0];
                if (marcada)
                    partes.push(`  - **Eu marquei:** ${umaLinha(textoDoMarcado(marcada.marcado, e.alternativas))}`);
                const certa = textoDoGabarito(e.tipo, e.gabarito, e.alternativas);
                // Cartão não tem certa: pedir itens novos com "Certa: (não se aplica)"
                // ensinaria o modelo a inventar gabarito para o que não tem.
                if (certa)
                    partes.push(`  - **Certa:** ${umaLinha(certa)}`);
                if (e.explicacao)
                    partes.push(`  - **Explicação:** ${umaLinha(e.explicacao)}`);
                partes.push(`  - **Errei ${e.erros} de ${e.tentativas} vez(es).**`);
                partes.push(`  - **Categoria:** ${e.categoria.join(" › ")}`);
            }
            partes.push("");
        }
    }
    partes.push("---");
    partes.push("");
    // O formato mora em `pedido-de-itens.ts`, com o pedido de lote novo: descrito
    // em dois lugares, ele diverge no primeiro campo que mudar.
    partes.push(blocoDoFormato({ trilha: d.trilha, lote: "erros-<data>", faixa: null }));
    partes.push("");
    return partes.join("\n");
}
/** Quebra de linha dentro de um item de lista quebraria a lista. */
function umaLinha(texto) {
    return texto.replace(/\s*\n+\s*/g, " ").trim();
}
//# sourceMappingURL=diagnostico.js.map